import { clamp } from '../geometry';
import type { XYPosition } from '../types';

/** Where a resize control sits: a side (line control) or a corner (handle control). */
export type ResizeControlPosition = 'top' | 'right' | 'bottom' | 'left' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

/** Node size and position during a resize, in v11 key order (`width, height, x, y` — it reaches the socket payload). */
export type ResizeValues = { width: number; height: number; x: number; y: number };

export type ResizeLimits = { minWidth: number; minHeight: number; maxWidth: number; maxHeight: number };

/** The outcome of one resize step: new values and which parts changed. */
export type ResizeStep = { values: ResizeValues; isSizeChange: boolean; isPositionChange: boolean };

const axesOf = (control: ResizeControlPosition) => ({
  enableX: control.includes('left') || control.includes('right'),
  enableY: control.includes('top') || control.includes('bottom'),
  invertX: control.includes('left'),
  invertY: control.includes('top'),
});

/**
 * Size and position after the pointer moved `delta` (flow units) from where the resize started (v11 rules):
 * whole-pixel steps, size clamped to the limits, and top/left controls move the node so the opposite side stays put.
 */
export const getResizeStep = (
  control: ResizeControlPosition,
  start: ResizeValues,
  previous: ResizeValues,
  delta: XYPosition,
  limits: ResizeLimits,
): ResizeStep => {
  const { enableX, enableY, invertX, invertY } = axesOf(control);
  const distX = Math.floor(enableX ? delta.x : 0);
  const distY = Math.floor(enableY ? delta.y : 0);
  const width = clamp(start.width + (invertX ? -distX : distX), limits.minWidth, limits.maxWidth);
  const height = clamp(start.height + (invertY ? -distY : distY), limits.minHeight, limits.maxHeight);
  const isWidthChange = width !== previous.width;
  const isHeightChange = height !== previous.height;
  const nextX = invertX ? start.x - (width - start.width) : start.x;
  const nextY = invertY ? start.y - (height - start.height) : start.y;
  const isXChange = nextX !== previous.x && isWidthChange;
  const isYChange = nextY !== previous.y && isHeightChange;
  return {
    values: { width, height, x: isXChange ? nextX : previous.x, y: isYChange ? nextY : previous.y },
    isSizeChange: isWidthChange || isHeightChange,
    isPositionChange: isXChange || isYChange,
  };
};

/** −1, 0 or 1 per axis: which way the node grew, seen from the dragged side (v11 `direction`). */
export const getResizeDirection = (control: ResizeControlPosition, previous: ResizeValues, next: ResizeValues): [number, number] => {
  const { invertX, invertY } = axesOf(control);
  const sign = (value: number) => Math.sign(value) + 0;
  return [sign(next.width - previous.width) * (invertX ? -1 : 1), sign(next.height - previous.height) * (invertY ? -1 : 1)];
};
